/**
 * The File / Folder choice for an ambiguous share path (plan Task 7.4, S7-10). A share SAS (`sr=s`) covers every file
 * and folder, so `…/share/report.csv` could be either: the parser answers the file candidate with `pathKind:
 * 'ambiguous'`, the form holds `azureStorageShareChoice: 'unset'` until the user (or `azure-storage:classify-share-path`)
 * picks, and Test / Save refuse meanwhile. The choice is transient form state — `withoutTransientStorageFields` keeps it
 * out of every connection the form builds; the saved `azureStorageScope` is always explicit.
 */
import type { StorageAttachmentScope, StorageResourceRef } from '@/types/azureStorage';
import type { ConnectionFormValues } from './connectionFormSchema';

export type SharePathKind = 'file' | 'directory';
type ShareChoiceValues = Pick<Partial<ConnectionFormValues>, 'azureStorageScope' | 'azureStorageShareChoice'>;

export const SHARE_PATH_REQUIRED = 'Say whether the path is a file or a folder: a share SAS covers both, so the URL alone cannot tell.';

const sharePathOf = (resource: StorageResourceRef | undefined) =>
  resource && (resource.kind === 'file' || resource.kind === 'directory') ? resource : undefined;

/** Scope + choice for a parsed resource URL; a re-blur of the same ambiguous path keeps the scope already chosen. */
export function sharePathFormValues(parsed: { resource: StorageResourceRef; pathKind?: 'ambiguous' }, current?: ShareChoiceValues): ShareChoiceValues {
  const scope: StorageAttachmentScope = { kind: 'resource', resource: parsed.resource };
  if (parsed.pathKind !== 'ambiguous') return { azureStorageScope: scope, azureStorageShareChoice: undefined };
  const kept = sharePathOf(current?.azureStorageScope?.kind === 'resource' ? current.azureStorageScope.resource : undefined);
  const candidate = sharePathOf(parsed.resource);
  if (kept && candidate && kept.share === candidate.share && kept.path === candidate.path && kept.snapshot === candidate.snapshot) {
    return { azureStorageScope: current?.azureStorageScope, azureStorageShareChoice: current?.azureStorageShareChoice };
  }
  return { azureStorageScope: scope, azureStorageShareChoice: 'unset' };
}

/** The explicit scope once the user (or the classification) says what the path is. */
export function chosenSharePathScope(scope: StorageAttachmentScope | undefined, kind: SharePathKind): StorageAttachmentScope | undefined {
  const path = sharePathOf(scope?.kind === 'resource' ? scope.resource : undefined);
  if (!path) return scope;
  return { kind: 'resource', resource: { kind, share: path.share, path: path.path, ...(path.snapshot ? { snapshot: path.snapshot } : {}) } };
}

/** Every connection the form builds (probe, save) drops the transient File / Folder choice and "Connect using" pick. */
export function withoutTransientStorageFields<T extends Partial<ConnectionFormValues>>(values: T): Omit<T, 'azureStorageShareChoice' | 'azureStorageModePick'> {
  const { azureStorageShareChoice: _choice, azureStorageModePick: _pick, ...rest } = values;
  void _choice;
  void _pick;
  return rest;
}
